import { Link } from 'react-router-dom'
import { Box, Building, DoorOpen, LayoutGrid } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { LocationRoom, LocationTree, PlacedUnit } from '@/api/facilityApi'
import type { CapacitySummaryRow } from '@/types/storage'

export type ElevationBuilding = LocationTree['facilities'][number]['buildings'][number]

/**
 * A building drawn flat as an elevation (z8nrz7fw25): a roof with its badge,
 * floors stacked as slabs from the top storey down, rooms standing on each
 * slab, and the ground with the facility's name under it — the explorer's 2D
 * view and the Locations search results. Read-only: floors and rooms are
 * added and changed in the explorer around it.
 */
export function BuildingElevation({
  building,
  facilityLabel,
  byEquipment,
  selectedRoomId,
  onSelectRoom,
  compact = false,
}: {
  building: ElevationBuilding
  /** Written on the ground line, e.g. "NYP · NewYork-Presbyterian". */
  facilityLabel: string
  /** Storage capacity by equipment id: a storage unit then links to its page and shows how full it is. */
  byEquipment?: Map<string, CapacitySummaryRow>
  selectedRoomId?: string | null
  onSelectRoom?: (roomId: string) => void
  /** Rooms as tiles with a unit count instead of the unit list. */
  compact?: boolean
}) {
  const floors = [...building.floors].sort((a, b) => b.level - a.level)

  return (
    <figure className='mx-auto w-full max-w-5xl' aria-label={`${building.name}, ${floors.length} floors`}>
      {/* Roof: the building's badge and name sit on it. */}
      <div className='relative mx-3 flex items-center gap-2.5 rounded-t-2xl border border-b-0 border-slate-300 bg-gradient-to-b from-slate-100 to-white px-4 py-2.5'>
        <span className='flex h-8 min-w-8 items-center justify-center rounded-lg bg-slate-800 px-2 text-sm font-bold text-white'>
          {building.code}
        </span>
        <figcaption className='min-w-0 flex-1 truncate text-sm font-semibold text-slate-900'>{building.name}</figcaption>
      </div>

      <div className='overflow-hidden rounded-b-sm border border-slate-300 bg-white'>
        {floors.length === 0 && <p className='px-6 py-10 text-center text-sm text-muted-foreground'>{building.name} has no floors yet.</p>}
        {floors.map((f, i) => (
          <section key={f.id} className={cn('grid grid-cols-[64px_minmax(0,1fr)]', i > 0 && 'border-t border-slate-200')}>
            {/* The level, like a lift indicator. */}
            <div className='flex flex-col items-center justify-center gap-0.5 border-r border-slate-200 bg-slate-50 py-4'>
              <span className='text-2xl font-semibold tabular-nums leading-none text-slate-800'>{f.level}</span>
              <span className='text-[10px] text-slate-500'>level</span>
            </div>
            <div className='min-w-0 px-4 py-3'>
              <div className='mb-2.5 flex items-center gap-2'>
                <h4 className='text-sm font-medium text-slate-900'>{f.name}</h4>
                <span className='text-xs text-slate-500'>
                  {f.rooms.length} {f.rooms.length === 1 ? 'room' : 'rooms'}
                </span>
              </div>
              <div className='flex flex-wrap items-stretch gap-2.5'>
                {f.rooms.map((r) => (
                  <RoomTile
                    key={r.id}
                    room={r}
                    byEquipment={byEquipment}
                    selected={selectedRoomId === r.id}
                    onSelect={onSelectRoom ? () => onSelectRoom(r.id) : undefined}
                    compact={compact}
                  />
                ))}
                {f.rooms.length === 0 && <p className='text-xs text-slate-500'>No rooms on this floor.</p>}
              </div>
            </div>
          </section>
        ))}
      </div>

      {/* Ground: where the building stands, and which facility it is in. */}
      <div
        className='border-x border-b border-slate-300 px-4 py-1.5 text-[11px] text-slate-600'
        style={{ backgroundImage: 'repeating-linear-gradient(135deg, rgb(226 232 240) 0 6px, rgb(241 245 249) 6px 12px)' }}
      >
        <span className='rounded bg-white/90 px-1.5 py-0.5 font-medium'>{facilityLabel}</span>
      </div>
    </figure>
  )
}

/**
 * A room on its floor. The bar on top says at a glance what is in it: blue for
 * a room holding equipment, grey for an empty one, amber when out of service.
 */
export function RoomTile({
  room,
  byEquipment,
  selected,
  onSelect,
  compact,
}: {
  room: LocationRoom
  byEquipment?: Map<string, CapacitySummaryRow>
  selected?: boolean
  onSelect?: () => void
  compact?: boolean
}) {
  const down = room.status === 'OUT_OF_SERVICE'
  const head = (
    <>
      <span
        className={cn('absolute inset-x-0 top-0 h-1 rounded-t-xl', down ? 'bg-amber-400' : room.units.length > 0 ? 'bg-primary' : 'bg-slate-200')}
        aria-hidden
      />
      <span className='flex items-baseline justify-between gap-2'>
        <span className='truncate text-sm font-semibold text-slate-900'>{room.label}</span>
        {room.units.length > 0 && (
          <span className='shrink-0 rounded-full bg-slate-100 px-1.5 text-[11px] font-medium tabular-nums text-slate-700'>
            {room.units.length}
          </span>
        )}
      </span>
      <span className='block truncate text-xs text-slate-500'>{room.name ?? room.typeName}</span>
      {down && <span className='mt-1 block text-[11px] font-medium text-amber-700'>Out of service</span>}
    </>
  )
  const frame = cn(
    'relative flex flex-col rounded-xl border bg-white px-3 pb-2.5 pt-3 text-left transition-colors',
    compact ? 'w-40' : 'w-full min-w-[220px] sm:w-64',
    selected ? 'border-primary ring-2 ring-primary/30' : 'border-slate-200',
  )

  if (compact || !room.units.length) {
    return onSelect ? (
      <button type='button' onClick={onSelect} className={cn(frame, 'hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary')} aria-pressed={selected}>
        {head}
        {!compact && <span className='mt-1.5 block text-xs text-slate-400'>Empty</span>}
      </button>
    ) : (
      <div className={frame}>
        {head}
        {!compact && <span className='mt-1.5 block text-xs text-slate-400'>Empty</span>}
      </div>
    )
  }

  return (
    <div className={frame}>
      {onSelect ? (
        <button type='button' onClick={onSelect} className='text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'>
          {head}
        </button>
      ) : (
        head
      )}
      <ul className='mt-2 space-y-1'>
        {room.units.map((u) => (
          <li key={u.id}>
            <UnitChip unit={u} capacity={byEquipment?.get(u.id)} />
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * A unit in a room. A storage unit opens its own page — the inside of its
 * address, Zone → Rack → Box — and shows how full it is.
 */
export function UnitChip({ unit, capacity }: { unit: PlacedUnit; capacity?: CapacitySummaryRow }) {
  const down = unit.status === 'OUT_OF_SERVICE' || !!capacity?.unavailable
  const pct = capacity && capacity.spaces > 0 ? Math.round((capacity.used / capacity.spaces) * 100) : null
  const body = (
    <>
      <span className={cn('h-2 w-2 shrink-0 rounded-full', down ? 'bg-amber-500' : 'bg-emerald-500')} aria-hidden />
      <span className='min-w-0 flex-1'>
        <span className='block truncate text-[13px] font-medium text-slate-900'>{unit.label}</span>
        <span className='block truncate text-[11px] text-slate-500'>
          {unit.typeName}
          {down && ' · not taking stock'}
        </span>
      </span>
      {pct !== null && (
        <span className='w-14 shrink-0 text-right' title={`${capacity?.used} of ${capacity?.spaces} positions used`}>
          <span className='block text-[11px] tabular-nums text-slate-700'>{pct}%</span>
          <span className='mt-0.5 block h-1 overflow-hidden rounded-full bg-slate-200'>
            <span
              className={cn('block h-full rounded-full', pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-primary')}
              style={{ width: `${pct}%` }}
            />
          </span>
        </span>
      )}
    </>
  )
  const cls = 'flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-2 py-1.5'
  return capacity ? (
    <Link
      to={`/inventory/storage/${capacity.nodeId}`}
      className={cn(cls, 'hover:border-primary hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary')}
    >
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  )
}

/** How the facility explorer draws a building: the whole building in 3D, one floor in 3D, or flat. */
export type ExplorerView = 'building' | '3d' | 'room' | '2d'

/** "Building 3D | Floor 3D | Room 3D | 2D". Room 3D needs a room chosen first. */
export function ViewToggle({
  value,
  onChange,
  roomEnabled = false,
}: {
  value: ExplorerView
  onChange: (v: ExplorerView) => void
  roomEnabled?: boolean
}) {
  return (
    <div role='tablist' aria-label='View' className='inline-flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1'>
      {(
        [
          ['building', 'Building 3D', Building],
          ['3d', 'Floor 3D', Box],
          ['room', 'Room 3D', DoorOpen],
          ['2d', '2D', LayoutGrid],
        ] as const
      ).map(([key, label, Icon]) => (
        <button
          key={key}
          type='button'
          role='tab'
          aria-selected={value === key}
          disabled={key === 'room' && !roomEnabled}
          title={key === 'room' && !roomEnabled ? 'Choose a room first' : undefined}
          onClick={() => onChange(key)}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40',
            value === key ? 'bg-primary font-medium text-primary-foreground shadow-sm' : 'text-slate-600 hover:text-slate-900',
          )}
        >
          <Icon className='h-3.5 w-3.5' /> {label}
        </button>
      ))}
    </div>
  )
}

/** A building in a list: its badge, name, and a small stack of its floors. */
export function BuildingThumb({
  building,
  selected,
  onSelect,
}: {
  building: ElevationBuilding
  selected: boolean
  onSelect: () => void
}) {
  const rooms = building.floors.reduce((n, f) => n + f.rooms.length, 0)
  const floors = [...building.floors].sort((a, b) => b.level - a.level).slice(0, 6)
  return (
    <button
      type='button'
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        selected ? 'border-primary bg-primary/5' : 'border-slate-200 bg-white hover:border-slate-300',
      )}
    >
      {/* The building in miniature: one bar per floor, filled when it has rooms. */}
      <span className='flex w-7 shrink-0 flex-col gap-0.5' aria-hidden>
        <span className='h-1 rounded-sm bg-slate-700' />
        {floors.length === 0 ? (
          <span className='h-2 rounded-sm border border-dashed border-slate-300' />
        ) : (
          floors.map((f) => <span key={f.id} className={cn('h-2 rounded-sm', f.rooms.length ? 'bg-primary/60' : 'bg-slate-200')} />)
        )}
      </span>
      <span className='min-w-0 flex-1'>
        <span className='flex items-center gap-1.5'>
          <span className='rounded bg-slate-800 px-1.5 text-[11px] font-bold text-white'>{building.code}</span>
          <span className='truncate text-sm font-medium text-slate-900'>{building.name}</span>
        </span>
        <span className='mt-0.5 block text-[11px] text-slate-500'>
          {building.floors.length} {building.floors.length === 1 ? 'floor' : 'floors'} · {rooms} {rooms === 1 ? 'room' : 'rooms'}
        </span>
      </span>
    </button>
  )
}
